import React, { useEffect } from 'react';
import Icon from '../common/Icon';
import { trackSubscriptionPromptViewed } from '../../utils/speakrootsAnalytics.js';

const CliffhangerPaywallModal = ({ isOpen, onClose, onViewPlans, paywallContext }) => {
  // Track subscription prompt viewed when modal opens
  useEffect(() => {
    if (isOpen) {
      trackSubscriptionPromptViewed('cliffhanger');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

      // Scroll to top smoothly when modal opens
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Prevent background scrolling
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    
    // CRITICAL CLEANUP - Prevents button timeout issues
      return () => {
        document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleEscape);
      
      // Force cleanup any lingering active states
      setTimeout(() => {
        const activeElements = document.querySelectorAll('[aria-expanded="true"]');
        activeElements.forEach(el => el.setAttribute('aria-expanded', 'false'));
        
        // Remove any stuck focus states
        const focusedElements = document.querySelectorAll(':focus');
        focusedElements.forEach(el => el.blur());
      }, 100);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Check if device is mobile (consistent with App.js breakpoint)
  const isMobile = window.innerWidth < 1024;

  const content = (
    <div className="text-center">
      <span className="text-6xl mb-4 block"><Icon name="rocket" /></span>
      <h2 className="text-2xl font-extrabold mb-2">You're on a roll!</h2>
      <p className="text-gray-600 mb-6">
        You've just completed all the free lessons in your trial! Unlock the rest of Unit {paywallContext?.unitNumber || 2} to continue your journey.
      </p>
      
      <div className="space-y-3 mb-6">
        <div className="flex items-center text-left">
          <span className="text-green-500 mr-3">✅</span>
          <span className="text-sm">Continue with {paywallContext?.nextItem ? `the '${paywallContext.nextItem}'` : 'the next activity'}</span>
        </div>
        <div className="flex items-center text-left">
          <span className="text-green-500 mr-3">✅</span>
          <span className="text-sm">Access all remaining lessons in Unit {paywallContext?.unitNumber || 2}</span>
        </div>
        <div className="flex items-center text-left">
          <span className="text-green-500 mr-3">✅</span>
          <span className="text-sm">Unlock all units and 100+ lessons</span>
        </div>
        <div className="flex items-center text-left">
          <span className="text-green-500 mr-3">✅</span>
          <span className="text-sm">Get unlimited Game Zone plays</span>
        </div>
      </div>

      <div className="space-y-3">
        <button
          onClick={onViewPlans}
          className="w-full bg-blue-500 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition active:scale-95"
        >
          Unlock Unit {paywallContext?.unitNumber || 2}
        </button>
        <button
          onClick={onClose}
          className="w-full text-gray-600 font-bold py-3 min-h-[44px]"
        >
          Maybe Later
        </button>
      </div>
    </div>
  );

  // Use bottom sheet on mobile, center modal on desktop
  if (isMobile) {
    return (
      <div 
        className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black bg-opacity-60"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div 
          className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-2xl font-bold mb-4">Continue Your Adventure!</h2>
          {content}
        </div>
      </div>
    );
  }

  // Desktop version (center modal)
  return (
    <div 
      className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black bg-opacity-60"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div 
        className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </div>
    </div>
  );
};

export default CliffhangerPaywallModal;